import { skipToken } from '@apollo/client'
import { ExternalLink } from 'lucide-react'
import { Suspense, useState } from 'react'
import { MigrationButtonLabel } from '@/components/layout/oci-migration-notice/migration-button-label'
import { SVL_OCI_MIGRATION_QUERY } from '@/components/layout/oci-migration-notice/queries'
import { WaveTimeline } from '@/components/layout/oci-migration-notice/wave-timeline'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { OCI_MIGRATION_DOCS_URL } from '@/config/oci-migration'
import { useIdCompany, useMixpanel, useSuspenseQuery } from '@/lib/hooks'
import { OciLogo } from '@/logos/oci'
import type {
  SvlOciMigrationQuery,
  SvlOciMigrationQueryVariables,
} from '@/components/layout/oci-migration-notice/queries.types.gen'
import type { SvlOciMigrationWave } from '@/graphql.types'

const WAVE_LABELS: Record<SvlOciMigrationWave, string> = {
  wave1: 'Wave 1',
  wave2: 'Wave 2',
}

const OciMigrationNoticeContent = () => {
  const { idCompany } = useIdCompany()
  const [isOpen, setOpen] = useState(false)
  const { track } = useMixpanel()

  const { data } = useSuspenseQuery<
    SvlOciMigrationQuery,
    SvlOciMigrationQueryVariables
  >(
    SVL_OCI_MIGRATION_QUERY,
    idCompany ? { variables: { idCompany } } : skipToken
  )

  const migration = data?.svlOciMigration
  const wave = migration?.wave

  if (!idCompany || !migration || !wave) {
    return null
  }

  const waveLabel = WAVE_LABELS[wave]
  const gcpClusterNames = migration.gcpClusters.map((cluster) => cluster.name)

  const handleOpen = () => {
    setOpen(true)
    track('OCI migration sheet opened', {
      wave: waveLabel,
      gcpClusters: gcpClusterNames,
      gcpClusterCount: gcpClusterNames.length,
      tcpProxyAppCount: migration.tcpProxyApps.length,
      externalDatabaseCount: migration.externalDatabases.length,
    })
  }

  const handleGuideClick = () => {
    track('OCI migration guide opened', { wave: waveLabel })
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="gap-2 h-8 px-3 text-xs font-medium"
        onClick={handleOpen}
      >
        <OciLogo className="h-3 w-auto" aria-hidden="true" />
        <span className="sr-only">Oracle Cloud</span>
        <Suspense fallback={<>Migration - {waveLabel}</>}>
          <MigrationButtonLabel idCompany={idCompany} waveLabel={waveLabel} />
        </Suspense>
      </Button>
      <Separator orientation="vertical" className="h-4!" />
      <Sheet open={isOpen} onOpenChange={setOpen}>
        <SheetContent className="sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-0">
            <SheetTitle className="flex items-center gap-2">
              <OciLogo className="h-4 w-auto" aria-hidden="true" />
              Migration to Oracle Cloud Infrastructure
            </SheetTitle>
            <SheetDescription>
              Sevalla is moving from Google Cloud to Oracle Cloud Infrastructure
              (OCI). Your account is scheduled for {waveLabel}. Your
              applications, deployment workflows and dashboard stay the same.
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-col gap-4 px-4 pb-4">
            <Button variant="outline" size="sm" className="self-start" asChild>
              <a
                href={OCI_MIGRATION_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleGuideClick}
              >
                Read the migration guide
                <ExternalLink aria-hidden="true" className="size-3.5" />
              </a>
            </Button>
            <WaveTimeline
              migration={migration}
              onNavigate={() => setOpen(false)}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}

export const OciMigrationNotice = () => (
  <Suspense fallback={null}>
    <OciMigrationNoticeContent />
  </Suspense>
)
